import { type ChangeEvent, useRef, useState } from 'react'

import {
  Divider,
  Flex,
  Form,
  IconAdd,
  IconDelete,
  IconUpload,
  PrimaryButton,
  SecondaryButton,
  Space,
  Tag,
  TertiaryButton,
  Text,
  TextField,
  TextLabel,
  TransparentIconButton,
  WithFormControlSelect,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { PrimaryCategorySelect } from '@webapp/core/components/PrimaryCategorySelect'
import { SecondaryCategorySelect } from '@webapp/core/components/SecondaryCategorySelect'
import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { type SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type Account, AccountSelect } from '@webapp/platform/finance'
import {
  type CreditCardItemFormAllocationValue,
  type CreditCardItemFormSchema,
  type ICreditCardItemFormProps,
} from './CreditCardItemForm.types'
import messages from './messages'

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024

const getRefLink = (item: CreditCardItem): Nullable<string> => {
  if (item.po) return `/#/orders/${item.po.uuid}`
  if (item.expense) return `/#/status/expense-report/${item.expense.uuid}`
  return null
}

const getInitialValues = (
  item: CreditCardItem | undefined
): CreditCardItemFormSchema => ({
  description: item?.description ?? '',
  date: item ? moment(item.receivedOn) : null,
  amount: item ? item.totalCost.toFixed(2) : '',
  currency: item?.currency ?? null,
  // `formatItem` mapping — allocation rows keyed by budgetId (BHV-018).
  allocations:
    item?.costAllocations.map((allocation, index) => ({
      rowId: `existing-${allocation.budgetId}-${index}`,
      budgetId: allocation.budgetId,
      label: `${allocation.primaryCategory.name} | ${allocation.secondaryCategory.name} | ${allocation.accountCodeLabel}`,
      primaryCategory: null,
      secondaryCategory: null,
      account: null,
      amount: parseFloat(allocation.amount).toFixed(2),
    })) ?? [],
  attachments: [],
})

const sumAllocations = (allocations: CreditCardItemFormAllocationValue[]) =>
  allocations.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)

export const CreditCardItemForm = ({
  mode,
  item,
  isSaving,
  onSubmit,
  onCancel,
}: ICreditCardItemFormProps) => {
  const intl = useProcurifyIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileSizeError, setFileSizeError] = useState(false)

  // PO/RFE-backed items keep their sourced description/date read-only in
  // edit mode (BHV-018); view mode is fully read-only except allocation
  // amounts (BHV-019).
  const hasSourceObject = Boolean(item?.po || item?.expense)
  const showEditableItemFields =
    mode === 'create' || (mode === 'edit' && !hasSourceObject)
  const canEditCostFields = mode !== 'view'

  const validationSchema = Yup.object({
    description: Yup.string().required(
      intl.formatMessage(messages.descriptionRequiredError)
    ),
    date: Yup.mixed()
      .nullable()
      .required(intl.formatMessage(messages.dateRequiredError)),
    amount: Yup.string().required(
      intl.formatMessage(messages.amountRequiredError)
    ),
    currency: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.currencyRequiredError)),
  })

  const formik = useFormik<CreditCardItemFormSchema>({
    initialValues: getInitialValues(item),
    // Submit-time inline errors (BHV-015) apply to the editable item fields.
    validationSchema: showEditableItemFields ? validationSchema : undefined,
    validateOnChange: false,
    validateOnBlur: false,
    // The async save is tracked by isSaving; isSubmitting is released
    // immediately so fields never latch disabled.
    onSubmit: (submittedValues, { setSubmitting }) => {
      onSubmit(submittedValues)
      setSubmitting(false)
    },
  })

  const { values, setFieldValue } = formik

  const estimatedTotal = Number(values.amount) || 0
  const unassignedCost = estimatedTotal - sumAllocations(values.allocations)
  const currencyLabel = values.currency?.name ?? ''

  const formatCost = (cost: number) =>
    `${intl.formatNumber(cost, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currencyLabel}`

  const handleAddAllocation = () => {
    setFieldValue('allocations', [
      ...values.allocations,
      {
        rowId: `new-${Date.now()}-${values.allocations.length}`,
        budgetId: null,
        label: null,
        primaryCategory: null,
        secondaryCategory: null,
        account: null,
        amount: '0.00',
      },
    ])
  }

  const handleRemoveAllocation = (index: number) => {
    setFieldValue(
      'allocations',
      values.allocations.filter(
        (_, allocationIndex) => allocationIndex !== index
      )
    )
  }

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Enforce the displayed 25.0 MB maximum per file (BHV-026).
    const acceptedFiles = files.filter(
      (file) => file.size <= MAX_FILE_SIZE_BYTES
    )
    setFileSizeError(acceptedFiles.length !== files.length)
    if (acceptedFiles.length > 0) {
      setFieldValue('attachments', [...values.attachments, ...acceptedFiles])
    }
    // Allow re-selecting the same file after a remove.
    event.target.value = ''
  }

  const handleRemoveAttachment = (index: number) => {
    setFieldValue(
      'attachments',
      values.attachments.filter((_, fileIndex) => fileIndex !== index)
    )
  }

  const refLink = item ? getRefLink(item) : null

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space size='large'>
          <Space size='small'>
            <Text bold autoMargin={false}>
              {intl.formatMessage(messages.itemInformationTitle)}
            </Text>

            {showEditableItemFields ? (
              <Space>
                <Form.TextField
                  id='description'
                  name='description'
                  label={intl.formatMessage(messages.descriptionLabel)}
                  placeholder={intl.formatMessage(
                    messages.descriptionPlaceholder
                  )}
                  required
                  autoFocus
                />
                <Flex.Row>
                  <Flex.Item width={1 / 3}>
                    <Form.SingleDatePicker
                      id='date'
                      name='date'
                      label={intl.formatMessage(messages.dateLabel)}
                      required
                      fullWidth
                      // Transaction dates are in the past — unblock
                      // react-dates' default future-only range.
                      isOutsideRange={() => false}
                    />
                  </Flex.Item>
                  <Flex.Item width={1 / 3}>
                    <Form.TextField
                      id='amount'
                      name='amount'
                      label={intl.formatMessage(messages.amountLabel)}
                      placeholder={intl.formatMessage(
                        messages.amountPlaceholder
                      )}
                      required
                    />
                  </Flex.Item>
                  <Flex.Item width={1 / 3}>
                    <WithFormControlSelect
                      id='currency'
                      name='currency'
                      label={intl.formatMessage(messages.currencyLabel)}
                      required
                    >
                      <CurrencySelect />
                    </WithFormControlSelect>
                  </Flex.Item>
                </Flex.Row>
              </Space>
            ) : (
              <Space
                direction='horizontal'
                justify='space-between'
                align='center'
              >
                <Space direction='horizontal' size='large' align='center'>
                  {item?.refLabel && refLink && (
                    <Space direction='horizontal' size='small' align='center'>
                      <Tag>
                        {item.po
                          ? intl.formatMessage(messages.poTag)
                          : intl.formatMessage(messages.rfeTag)}
                      </Tag>
                      <BridgeLink to={refLink}>
                        {item.po?.number ??
                          String(item.expense?.expenseReportId ?? '')}
                      </BridgeLink>
                    </Space>
                  )}
                  {item && (
                    <Text autoMargin={false}>
                      <FormattedShortDate date={item.receivedOn} />
                    </Text>
                  )}
                  <Space size='small'>
                    <TextLabel>
                      {intl.formatMessage(messages.descriptionLabel)}
                    </TextLabel>
                    <Text autoMargin={false}>{item?.description}</Text>
                  </Space>
                </Space>
                <Space size='small'>
                  <TextLabel>
                    {intl.formatMessage(messages.totalCostLabel)}
                  </TextLabel>
                  <Space direction='horizontal' size='small' align='center'>
                    <TextField
                      value={values.amount}
                      disabled={!canEditCostFields}
                      onChange={(event) =>
                        setFieldValue('amount', event.target.value)
                      }
                      inputProps={{
                        'aria-label': intl.formatMessage(
                          messages.totalCostAriaLabel
                        ),
                      }}
                    />
                    <Text autoMargin={false}>{currencyLabel}</Text>
                  </Space>
                </Space>
              </Space>
            )}
          </Space>

          {/* Attachments — uploaded on save, UUIDs into packing_slips (BHV-017) */}
          <Space size='small'>
            <TextLabel>
              {intl.formatMessage(messages.attachmentsTitle)}
            </TextLabel>
            {item?.packingSlips.map((slip) => (
              <Text key={slip.uuid} autoMargin={false}>
                {slip.name}
              </Text>
            ))}
            {values.attachments.map((file, index) => (
              <Space
                key={`${file.name}-${index}`}
                direction='horizontal'
                size='small'
                align='center'
              >
                <Text autoMargin={false}>{file.name}</Text>
                <TransparentIconButton
                  iconComponent={<IconDelete />}
                  type='button'
                  aria-label={intl.formatMessage(
                    messages.removeAttachmentAriaLabel,
                    { name: file.name }
                  )}
                  onClick={() => handleRemoveAttachment(index)}
                />
              </Space>
            ))}
            {values.attachments.length === 0 &&
              (item?.packingSlips.length ?? 0) === 0 && (
                <Text color='textSecondary' autoMargin={false}>
                  {intl.formatMessage(messages.noFilesAttachedText)}
                </Text>
              )}
            {canEditCostFields && (
              <Space direction='horizontal' size='small' align='center'>
                <input
                  ref={fileInputRef}
                  type='file'
                  multiple
                  hidden
                  aria-hidden
                  onChange={handleFilesSelected}
                  data-testid='credit-card-item-form-file-input'
                />
                <SecondaryButton
                  type='button'
                  iconComponent={<IconUpload />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {intl.formatMessage(messages.uploadButton)}
                </SecondaryButton>
              </Space>
            )}
            {fileSizeError && (
              <Text color='alert' autoMargin={false}>
                {intl.formatMessage(messages.fileTooLargeError)}
              </Text>
            )}
          </Space>

          {/* Allocate to — live total estimated / unassigned cost (BHV-017) */}
          <Space size='small'>
            <Space
              direction='horizontal'
              justify='space-between'
              align='center'
            >
              <Text bold autoMargin={false}>
                {intl.formatMessage(messages.allocateToTitle)}
              </Text>
              <Text bold autoMargin={false}>
                {intl.formatMessage(messages.costColumnTitle)}
              </Text>
            </Space>

            {values.allocations.map((row, index) => (
              <Space
                key={row.rowId}
                direction='horizontal'
                justify='space-between'
                align='center'
              >
                {row.budgetId !== null ? (
                  <Text autoMargin={false}>{row.label}</Text>
                ) : (
                  <Space direction='horizontal' size='small'>
                    <Space.Item minWidth={160}>
                      <PrimaryCategorySelect
                        value={row.primaryCategory}
                        placeholder={intl.formatMessage(
                          messages.locationPlaceholder
                        )}
                        aria-label={intl.formatMessage(
                          messages.locationAriaLabel
                        )}
                        onChange={(option: Nullable<PrimaryCategory>) => {
                          setFieldValue(`allocations[${index}]`, {
                            ...row,
                            primaryCategory: option,
                            secondaryCategory: null,
                            account: null,
                          })
                        }}
                      />
                    </Space.Item>
                    <Space.Item minWidth={160}>
                      <SecondaryCategorySelect
                        key={row.primaryCategory?.id}
                        primaryCategoryId={row.primaryCategory?.id}
                        value={row.secondaryCategory}
                        placeholder={intl.formatMessage(
                          messages.departmentPlaceholder
                        )}
                        aria-label={intl.formatMessage(
                          messages.departmentAriaLabel
                        )}
                        onChange={(option: Nullable<SecondaryCategory>) => {
                          setFieldValue(`allocations[${index}]`, {
                            ...row,
                            secondaryCategory: option,
                            account: null,
                          })
                        }}
                      />
                    </Space.Item>
                    <Space.Item minWidth={200}>
                      <AccountSelect
                        secondaryCategoryId={row.secondaryCategory?.id}
                        value={row.account}
                        placeholder={intl.formatMessage(
                          messages.accountCodePlaceholder
                        )}
                        aria-label={intl.formatMessage(
                          messages.accountCodeAriaLabel
                        )}
                        isDisabled={!row.secondaryCategory}
                        onChange={(option: Nullable<Account>) => {
                          setFieldValue(`allocations[${index}].account`, option)
                        }}
                      />
                    </Space.Item>
                  </Space>
                )}
                <Space direction='horizontal' size='small' align='center'>
                  <TextField
                    value={row.amount}
                    onChange={(event) =>
                      setFieldValue(
                        `allocations[${index}].amount`,
                        event.target.value
                      )
                    }
                    inputProps={{
                      'aria-label': `${intl.formatMessage(
                        messages.allocationAmountAriaLabel
                      )} ${index + 1}`,
                    }}
                  />
                  <Text autoMargin={false}>{currencyLabel}</Text>
                  {canEditCostFields && row.budgetId === null && (
                    <TransparentIconButton
                      iconComponent={<IconDelete />}
                      type='button'
                      aria-label={`${intl.formatMessage(
                        messages.removeAllocationAriaLabel
                      )} ${index + 1}`}
                      onClick={() => handleRemoveAllocation(index)}
                    />
                  )}
                </Space>
              </Space>
            ))}

            {canEditCostFields && (
              <Space direction='horizontal'>
                <SecondaryButton
                  type='button'
                  iconComponent={<IconAdd />}
                  onClick={handleAddAllocation}
                >
                  {intl.formatMessage(messages.addAccountCodeButton)}
                </SecondaryButton>
              </Space>
            )}

            <Space size='small' align='end'>
              <Space direction='horizontal' size='small' align='center'>
                <TextLabel>
                  {intl.formatMessage(messages.totalEstimatedCostLabel)}
                </TextLabel>
                <Text bold autoMargin={false}>
                  {formatCost(estimatedTotal)}
                </Text>
              </Space>
              <Space direction='horizontal' size='small' align='center'>
                <TextLabel>
                  {intl.formatMessage(messages.unassignedCostLabel)}
                </TextLabel>
                <Text bold autoMargin={false}>
                  {formatCost(unassignedCost)}
                </Text>
              </Space>
            </Space>
          </Space>

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.saveButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

CreditCardItemForm.displayName = 'CreditCardItemForm'
